import { Link, useOutletContext } from 'react-router';
import {
  Badge,
  Metric,
  Panel,
  cn,
  PRESS,
  formatDate,
  formatMoney,
  type BadgeTone,
} from 'escrowbuild-ui';

import {
  useFixtureVocabulary,
  useMilestones,
  usePublications,
  usePublishedPublications,
  useT,
  type AppTranslate,
  type AppTranslationKey,
} from '@/i18n';
import {
  MILESTONES,
  participationsOf,
  projectTotals,
  type Milestone,
  type Project,
  type Publication,
  type PublicationStatus,
} from '@/data/domain';

/**
 * The milestones whose subject is an inspection, matched on the fixture's own
 * English rather than on the words on screen.
 *
 * The sentence below says whether the next thing due is a vistoria. Which
 * milestone that is cannot be read off the rendered title once the title is in
 * another language, so the set is built from the English source of truth and the
 * display stays free to be translated.
 */
const INSPECTION_MILESTONES = new Set(
  MILESTONES.filter((milestone) => milestone.title.includes('inspection')).map(
    (milestone) => milestone.id,
  ),
);

interface ProjectOutlet {
  project: Project;
  totals: ReturnType<typeof projectTotals>;
}

const STATE_TONE: Record<PublicationStatus, BadgeTone> = {
  draft: 'neutral',
  'in-review': 'warning',
  approved: 'info',
  published: 'success',
};

const STATE_LABEL: Record<PublicationStatus, AppTranslationKey> = {
  draft: 'status.publication.draft',
  'in-review': 'status.publication.inReview',
  approved: 'status.publication.approved',
  published: 'status.publication.published',
};

const LINK = cn(
  'font-medium text-heading underline decoration-line-strong underline-offset-2',
  'transition-colors duration-[var(--dur-fast)] hover:decoration-brand',
  PRESS,
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
);

/**
 * Only the steps that actually happened, in the order they happened.
 *
 * The translator arrives as an argument rather than being reached for: this is
 * not a component, so it may not call `useT()`, and the chain is assembled from
 * four separate sentences that each have to be translated on their own.
 */
function chainOf(record: Publication, t: AppTranslate): string {
  const parts = [t('project.publications.preparedBy', { name: record.preparedBy })];
  if (record.reviewedBy) {
    parts.push(t('project.publications.reviewedBy', { name: record.reviewedBy }));
  }
  if (record.approvedBy) {
    parts.push(
      t('project.publications.approvedBy', {
        name: record.approvedBy,
        date: formatDate(record.approvedAt),
      }),
    );
  }
  if (record.publishedAt) {
    parts.push(
      t('project.publications.releasedOn', { date: formatDate(record.publishedAt) }),
    );
  }
  return parts.join(' · ');
}

/**
 * A milestone's line: when it is due, and the phase it belongs to.
 *
 * The phase is resolved rather than printed as its id. Printing the id put a
 * wire value on screen — `framing` — which happens to read as an English word
 * and is not one the language selector could reach.
 */
function milestoneNote(milestone: Milestone, phaseName: (phaseId: string) => string): string {
  return `${formatDate(milestone.date)} · ${phaseName(milestone.phaseId)}`;
}

/**
 * The publishing record of one development, seen from both sides at once.
 *
 * The two columns are the same queue read under two different
 * rules: the internal queue shows every state, the investor feed shows the ones
 * that cleared the gate. That difference is the product, so the screen puts the
 * two views beside each other rather than describing the rule in a caption.
 */
export function ProjectPublicationsPage() {
  const { project } = useOutletContext<ProjectOutlet>();
  const t = useT();

  /* The queue, the investor feed and the milestone list, in the reader's
     language — a publication's title and body and a milestone's title are the
     records' own words. */
  const queue = usePublications(project.id);
  const published = usePublishedPublications(project.id);
  const milestones = useMilestones(project.id);
  const vocabulary = useFixtureVocabulary();

  const notified = milestones.filter((milestone) => milestone.status === 'reached' && milestone.notified);
  const quiet = milestones.filter((milestone) => milestone.status === 'reached' && !milestone.notified);
  const ahead = milestones.filter((milestone) => milestone.status === 'upcoming');
  const audience = participationsOf(project.id).length;
  const quietReleases = published.filter((record) => !record.notify).length;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <Metric
          label={t('project.publications.queueMetric')}
          value={queue.length}
          format="number"
          hint={t('project.publications.queueHint')}
        />
        <Metric
          label={t('project.publications.visibleMetric')}
          value={published.length}
          format="number"
          tone="success"
          hint={t('project.publications.visibleHint', { total: queue.length })}
        />
        <Metric
          label={t('project.publications.hiddenMetric')}
          value={queue.length - published.length}
          format="number"
          tone="warning"
          hint={t('project.publications.hiddenHint')}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Panel
          title={t('project.publications.queueTitle')}
          description={t('project.publications.queueDescription')}
        >
          <ul className="flex list-none flex-col gap-4 p-0">
            {queue.map((record) => (
              <li key={record.id} className="border-b border-line-subtle pb-4 last:border-b-0 last:pb-0">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-heading">{record.title}</span>
                    <span className="block text-2xs text-muted">
                      {record.period} · {chainOf(record, t)}
                    </span>
                  </span>
                  <Badge tone={STATE_TONE[record.status]} variant="subtle" size="sm">
                    {t(STATE_LABEL[record.status])}
                  </Badge>
                </div>

                <p className="mt-1.5 text-2xs text-muted">
                  {t('project.publications.queueItemMeta', {
                    photos: t('project.publications.photos', { count: record.photos }),
                    documents: t('project.publications.documents', { count: record.documents }),
                    amount: formatMoney(record.spendToDate),
                    variance: formatMoney(record.variance),
                  })}
                </p>

                <p className="mt-1.5 text-2xs">
                  {record.status === 'published' ? (
                    <span className={record.notify ? 'text-success' : 'text-muted'}>
                      {record.notify
                        ? t('project.publications.releasedNotified', {
                            date: formatDate(record.publishedAt),
                          })
                        : t('project.publications.releasedQuietly', {
                            date: formatDate(record.publishedAt),
                          })}
                    </span>
                  ) : (
                    <span className="text-warning">{t('project.publications.stillInternal')}</span>
                  )}
                </p>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel
          title={t('project.publications.feedTitle')}
          description={t('project.publications.feedDescription', { project: project.name })}
        >
          {published.length === 0 ? (
            <p className="text-sm text-muted">{t('project.publications.feedEmpty')}</p>
          ) : (
            <ul className="flex list-none flex-col gap-4 p-0">
              {published.map((record) => (
                <li
                  key={record.id}
                  className="rounded-xs border border-line bg-surface-alt p-3"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <span className="min-w-0">
                      <span className="block font-display text-sm font-semibold uppercase tracking-title text-heading">
                        {record.title}
                      </span>
                      <span className="block text-2xs text-muted">
                        {project.name} · {record.period} ·{' '}
                        {t('project.publications.releasedOn', {
                          date: formatDate(record.publishedAt),
                        })}
                      </span>
                    </span>
                    <Badge tone={record.notify ? 'success' : 'neutral'} variant="subtle" size="sm">
                      {record.notify
                        ? t('project.publications.notificationSent')
                        : t('project.publications.quietRelease')}
                    </Badge>
                  </div>
                  <p className="mt-2 text-sm text-body">{record.body}</p>
                  <p className="mt-2 text-2xs text-muted">
                    {t('project.publications.feedItemMeta', {
                      photographs: t('project.publications.photographs', {
                        count: record.photos,
                      }),
                      documents: t('project.publications.documents', { count: record.documents }),
                      amount: formatMoney(record.spendToDate),
                    })}
                  </p>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-4 text-2xs text-muted">
            {t('project.publications.audience', {
              count: audience,
              records: queue.length - published.length,
            })}
          </p>
        </Panel>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Panel
          title={t('project.publications.notifiedTitle')}
          description={t('project.publications.notifiedDescription', {
            count: notified.length,
            total: milestones.length,
          })}
        >
          {notified.length === 0 ? (
            <p className="text-sm text-muted">{t('project.publications.notifiedEmpty')}</p>
          ) : (
            <ul className="flex list-none flex-col gap-3 p-0">
              {notified.map((milestone) => (
                <li key={milestone.id} className="flex items-start justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block text-sm text-body">{milestone.title}</span>
                    <span className="block text-2xs text-muted">{milestoneNote(milestone, vocabulary.phase)}</span>
                  </span>
                  <Badge tone="success" variant="subtle" size="sm">
                    {t('project.publications.notifiedBadge')}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title={t('project.publications.quietTitle')}
          description={t('project.publications.quietDescription')}
        >
          {quiet.length === 0 ? (
            <p className="text-sm text-muted">{t('project.publications.quietEmpty')}</p>
          ) : (
            <ul className="flex list-none flex-col gap-3 p-0">
              {quiet.map((milestone) => (
                <li key={milestone.id} className="flex items-start justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block text-sm text-body">{milestone.title}</span>
                    <span className="block text-2xs text-muted">{milestoneNote(milestone, vocabulary.phase)}</span>
                  </span>
                  <Badge tone="neutral" variant="subtle" size="sm">
                    {t('project.publications.quietBadge')}
                  </Badge>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-4 text-2xs text-muted">
            {t('project.publications.silenceNote', {
              milestones: t('project.publications.countMilestones', { count: quiet.length }),
              updates: t('project.publications.countUpdates', { count: quietReleases }),
            })}
          </p>

          {ahead.length > 0 ? (
            <p className="mt-3 text-2xs text-muted">
              {t('project.publications.ahead', {
                count: ahead.length,
                next: ahead.some((milestone) => INSPECTION_MILESTONES.has(milestone.id))
                  ? t('project.publications.nextInspection')
                  : t('project.publications.nextOne'),
                date: formatDate(ahead[0].date),
              })}
            </p>
          ) : null}
        </Panel>
      </div>

      <Panel
        title={t('project.publications.otherSideTitle')}
        description={t('project.publications.otherSideDescription')}
      >
        <p className="text-sm text-body">
          <Link to={`/portal/projects/${project.id}`} className={LINK}>
            {t('project.publications.viewAsInvestor', { project: project.name })}
          </Link>{' '}
          {t('project.publications.holderNote', { count: published.length })}
        </p>
      </Panel>
    </div>
  );
}
